import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import './LegalPages.css';

/**
 * PrivacyPolicy Component
 * 
 * Legal page explaining data collection and processing.
 * Complies with Colombia's Ley 1581 (Habeas Data).
 * Explains Postgres RLS isolation and Google OAuth data handling.
 */
function PrivacyPolicy() {
    const currentYear = new Date().getFullYear();

    return (
        <div className="legal-page">
            <div className="legal-container">
                {/* Header */}
                <div className="legal-header">
                    <div className="legal-icon">
                        <Lock size={48} className="icon-emerald" />
                    </div>
                    <h1 className="legal-title">Política de Privacidad y Protección de Datos</h1>
                    <p className="legal-subtitle">
                        Última actualización: {currentYear}
                    </p>
                </div>

                {/* Habeas Data Notice */}
                <div className="legal-notice habeas-data-notice">
                    <h3>🇨🇴 Cumplimiento Ley 1581 de 2012 (Habeas Data)</h3>
                    <p>
                        Esta Política de Privacidad cumple con la <strong>Ley 1581 de 2012</strong> de Colombia
                        y su Decreto Reglamentario 1377 de 2013, que establece el régimen general de protección
                        de datos personales en Colombia.
                    </p>
                </div>

                {/* Content */}
                <div className="legal-content">
                    <section className="legal-section">
                        <h2>1. Responsable del Tratamiento de Datos</h2>
                        <p>
                            <strong>Responsable:</strong> CottonTopTech<br />
                            <strong>Producto:</strong> Cresta<br />
                            <strong>Domicilio:</strong> Cali, Valle del Cauca, Colombia<br />
                            <strong>Correo de contacto:</strong>{' '}
                            <a href="mailto:info@cottontoptech.com" className="legal-link">info@cottontoptech.com</a>
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>2. Datos que Recolectamos</h2>
                        <p>
                            Cresta recolecta únicamente los siguientes datos personales necesarios para prestar el servicio:
                        </p>

                        <h3>2.1 Datos del Usuario (Dueño del Salón)</h3>
                        <ul>
                            <li><strong>Correo electrónico:</strong> Obtenido a través de Google OAuth para autenticación</li>
                            <li><strong>Nombre completo:</strong> Proporcionado por Google o ingresado durante el registro</li>
                            <li><strong>Foto de perfil:</strong> Opcional, obtenida de Google</li>
                        </ul>

                        <h3>2.2 Datos de los Clientes del Salón</h3>
                        <p>Información que TÚ (como dueño del salón) ingresas voluntariamente:</p>
                        <ul>
                            <li>Nombre del cliente</li>
                            <li>Teléfono (para notificaciones de citas)</li>
                            <li>Correo electrónico (opcional)</li>
                            <li><strong>Datos Fiscales:</strong> Cédula, NIT, dirección y ciudad (para Facturación Electrónica DIAN)</li>
                            <li>Historial de citas y servicios</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>3. Finalidad del Tratamiento de Datos</h2>
                        <p>Utilizamos los datos personales exclusivamente para:</p>
                        <ul>
                            <li><strong>Autenticación:</strong> Verificar tu identidad mediante Google OAuth</li>
                            <li><strong>Prestación del servicio:</strong> Permitirte gestionar tu salón (agenda, clientes, inventario)</li>
                            <li><strong>Notificaciones:</strong> Enviar recordatorios de citas a tus clientes vía WhatsApp/Email</li>
                            <li><strong>Soporte técnico:</strong> Asistencia en caso de problemas con la plataforma</li>
                        </ul>
                        <p>
                            <strong>NO vendemos, alquilamos ni compartimos tus datos con terceros para fines comerciales.</strong>
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>4. Seguridad y Aislamiento de Datos (RLS)</h2>
                        <p>
                            Implementamos <strong>Row Level Security (RLS) en PostgreSQL</strong> para garantizar
                            el aislamiento estricto de los datos entre salones. Esto significa que:
                        </p>
                        <ul>
                            <li>Los datos de tu salón están completamente aislados de otros usuarios</li>
                            <li>Ningún otro usuario puede acceder a tu información, ni siquiera por error</li>
                            <li>La seguridad se aplica a nivel de base de datos, no solo en la aplicación</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>5. Compartición de Datos con Terceros</h2>
                        <p>
                            Cresta integra servicios de terceros únicamente para funciones específicas:
                        </p>

                        <h3>5.1 Google (OAuth)</h3>
                        <ul>
                            <li><strong>Propósito:</strong> Autenticación de usuarios</li>
                            <li><strong>Datos compartidos:</strong> Email, nombre, foto (con tu consentimiento al hacer login)</li>
                            <li><strong>Política de Google:</strong>{' '}
                                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="legal-link">
                                    https://policies.google.com/privacy
                                </a>
                            </li>
                        </ul>

                        <h3>5.2 Twilio (WhatsApp)</h3>
                        <ul>
                            <li><strong>Propósito:</strong> Envío de notificaciones de citas</li>
                            <li><strong>Datos compartidos:</strong> Número de teléfono del cliente y mensaje de confirmación</li>
                            <li><strong>Política de Twilio:</strong>{' '}
                                <a href="https://www.twilio.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="legal-link">
                                    https://www.twilio.com/legal/privacy
                                </a>
                            </li>
                        </ul>

                        <h3>5.3 Resend (Email)</h3>
                        <ul>
                            <li><strong>Propósito:</strong> Envío de correos transaccionales (verificación, notificaciones)</li>
                            <li><strong>Datos compartidos:</strong> Dirección de correo electrónico</li>
                        </ul>

                        <h3>5.4 Cloudinary (Almacenamiento y Optimización)</h3>
                        <ul>
                            <li><strong>Propósito:</strong> Almacenamiento seguro de imágenes (logos de salón, fotos de perfil) y optimización de entrega.</li>
                            <li><strong>Política de Cloudinary:</strong>{' '}
                                <a href="https://cloudinary.com/privacy" target="_blank" rel="noopener noreferrer" className="legal-link">
                                    https://cloudinary.com/privacy
                                </a>
                            </li>
                        </ul>

                        <h3>5.5 Wompi - Bancolombia (Pagos)</h3>
                        <ul>
                            <li><strong>Propósito:</strong> Procesamiento seguro de pagos y suscripciones.</li>
                            <li><strong>Datos compartidos:</strong> Cresta NO almacena tarjetas de crédito. Los datos se envían directamente a Wompi de forma cifrada.</li>
                            <li><strong>Política de Wompi:</strong>{' '}
                                <a href="https://wompi.com/es/co/legal/" target="_blank" rel="noopener noreferrer" className="legal-link">
                                    https://wompi.com/es/co/legal/
                                </a>
                            </li>
                        </ul>

                        <h3>5.6 Proveedores de Facturación Electrónica (Opcional)</h3>
                        <ul>
                            <li><strong>Propósito:</strong> Cumplimiento legal ante la DIAN.</li>
                            <li><strong>Nota:</strong> Si integras tu cuenta de Alegra o Siigo, Cresta compartirá los datos de venta para generar el documento legal bajo tu autorización.</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>6. Tus Derechos (Habeas Data)</h2>
                        <p>
                            De acuerdo con la <strong>Ley 1581 de 2012</strong>, tienes derecho a:
                        </p>
                        <ul>
                            <li><strong>Conocer, actualizar y rectificar</strong> tus datos personales</li>
                            <li><strong>Solicitar prueba de la autorización</strong> otorgada para el tratamiento de datos</li>
                            <li><strong>Ser informado</strong> del uso que se ha dado a tus datos</li>
                            <li><strong>Revocar la autorización</strong> y/o solicitar la supresión de tus datos
                                (excepto cuando exista una obligación legal de conservarlos)</li>
                            <li><strong>Acceder de forma gratuita</strong> a tus datos</li>
                        </ul>
                        <p>
                            Para ejercer estos derechos, envía un correo a:{' '}
                            <a href="mailto:info@cottontoptech.com" className="legal-link">info@cottontoptech.com</a>
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>7. Retención y Eliminación de Datos</h2>
                        <ul>
                            <li><strong>Datos de usuario:</strong> Se conservan mientras tu cuenta esté activa</li>
                            <li><strong>Datos de clientes:</strong> Se conservan mientras utilices el servicio o según lo requiera la ley</li>
                            <li><strong>Eliminación:</strong> Puedes solicitar la eliminación completa de tu cuenta y datos
                                asociados en cualquier momento</li>
                        </ul>
                        <p className="legal-warning">
                            ⚠️ Durante la fase BETA, no garantizamos la recuperación de datos eliminados accidentalmente.
                            Te recomendamos realizar exportaciones periódicas de tu información crítica.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>8. Cookies y Tecnologías de Seguimiento</h2>
                        <p>
                            Utilizamos únicamente cookies esenciales para el funcionamiento de la autenticación (JWT tokens).
                            No utilizamos cookies de publicidad ni de seguimiento de terceros.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>9. Transferencias Internacionales</h2>
                        <p>
                            Nuestros servidores se encuentran en Estados Unidos (Render.com, Cloudflare).
                            Al aceptar esta Política, autorizas expresamente la transferencia de tus datos
                            a estos países, donde se aplicarán medidas de seguridad equivalentes a las exigidas
                            por la legislación colombiana.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>10. Modificaciones a esta Política</h2>
                        <p>
                            Nos reservamos el derecho de actualizar esta Política de Privacidad. Los cambios
                            se notificarán a través de la plataforma o por correo electrónico. La fecha de
                            "Última actualización" al inicio de este documento reflejará los cambios.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>11. Contacto</h2>
                        <p>
                            Para preguntas, solicitudes o reclamos relacionados con la protección de datos personales:
                        </p>
                        <ul>
                            <li><strong>Email:</strong>{' '}
                                <a href="mailto:info@cottontoptech.com" className="legal-link">info@cottontoptech.com</a>
                            </li>
                            <li><strong>Asunto:</strong> "Protección de Datos Personales - [Tu Nombre]"</li>
                        </ul>
                        <p>
                            Atenderemos tu solicitud en un plazo máximo de 15 días hábiles, según lo establecido por la ley.
                        </p>
                    </section>
                </div>

                {/* Footer Actions */}
                <div className="legal-footer">
                    <Link to="/" className="btn-secondary">
                        Volver al Inicio
                    </Link>
                    <Link to="/terms" className="btn-link">
                        Ver Términos de Servicio
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default PrivacyPolicy;
